
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CartItem, Order } from '../types';
import { orderHistoryService } from '../services/orderHistoryService';

interface OrderConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  orderType: 'takeaway' | 'dine-in' | 'delivery';
  items: CartItem[];
  customerName?: string;
  tableNumber?: string;
  deliveryAddress?: string;
  complementaryInfo?: string;
  phoneNumber?: string;
  paymentMethod?: 'cash' | 'card';
  invoiceData?: any;
}

export const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  isOpen,
  onClose,
  orderType,
  items,
  customerName,
  tableNumber,
  deliveryAddress,
  complementaryInfo,
  phoneNumber,
  paymentMethod,
  invoiceData,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(20);
  const [progress, setProgress] = useState(100);
  const [showContent, setShowContent] = useState(false);
  const [orderTotals, setOrderTotals] = useState({
    invoiceTotal: 0,
  });
  const [orderCode, setOrderCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
    

  // Calcul des totaux
  useEffect(() => {
    if (invoiceData) {
      const invoiceTotal = invoiceData.displayTotal || items.reduce((sum, item) => {
        const itemPrice = item.menuItem.price;
        const customizationPrice = Object.values(item.customizations).reduce(
          (optSum, options) => optSum + options.reduce((sum, opt) => sum + opt.price, 0),
          0
        );
        return sum + (itemPrice + customizationPrice) * item.quantity;
      }, 0);
      console.log('Mise à jour de orderTotals :', { invoiceTotal });
      setOrderTotals({ invoiceTotal });
    }
  }, [invoiceData, items]);

  // Fetch orderCode from API
  useEffect(() => {
    const fetchOrderData = async () => {
      const effectiveOrderId = invoiceData?.order_id;
      if (!effectiveOrderId || !invoiceData) {
        console.log('orderId ou invoiceData manquant', {
          effectiveOrderId,
          hasInvoiceData: !!invoiceData,
        });
        setOrderCode('N/A');
        return;
      }

      const payload = {
        store_id: invoiceData.store?._id,
        server_token: invoiceData.store?.server_token,
        order_id: effectiveOrderId,
      };

      console.log('Lancement du fetch avec payload :', JSON.stringify(payload, null, 2));

      try {
        const response = await fetch('https://api.restomi.fr/v3/api/store/get_order_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Erreur HTTP : ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        console.log('Réponse complète de l’API :', JSON.stringify(data, null, 2));

        const uniqueId = data.order?.unique_id || data.order_payment?.unique_id || 'N/A';
        setOrderCode(uniqueId.toString());
      } catch (error) {
        console.error('Erreur lors du fetch :', error);
        setOrderCode('Erreur');
        setError(t('Échec de la récupération du numéro de commande'));
      }
    };

    if (isOpen && !hasFetched.current) {
      console.log('Conditions remplies, lancement de fetchOrderData');
      hasFetched.current = true;
      fetchOrderData();
    }
  }, [isOpen, invoiceData, t]);

  // Sauvegarde dans localStorage
  useEffect(() => {
    if (
      orderCode &&
      orderCode !== 'N/A' &&
      orderCode !== 'Erreur' &&
      orderTotals.invoiceTotal > 0
    ) {
      const order: Order = {
        id: orderCode,
        items: items.map((item) => ({
          menuItem: {
            id: item.menuItem.id,
            name: item.menuItem.name,
            price: item.menuItem.price,
          },
          quantity: item.quantity,
          customizations: item.customizations,
          uniqueId: item.uniqueId,
        })),
        total: orderTotals.invoiceTotal,
        status: 'pending',
        createdAt: new Date().toISOString(),
        customerId: customerName || 'anonymous',
        orderType,
        tableNumber,
        deliveryAddress,
        complementaryInfo,
        phoneNumber,
        paymentMethod,
      };

      orderHistoryService.saveOrder(order);
    }
  }, [
    orderCode,
    orderTotals,
    items,
    customerName,
    orderType,
    tableNumber,
    deliveryAddress,
    complementaryInfo,
    phoneNumber,
    paymentMethod,
  ]);

  // Gestion de l'affichage
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setShowContent(true), 100);
    } else {
      setShowContent(false);
      hasFetched.current = false;
    }
  }, [isOpen]);

  // Gestion du compte à rebours
  const handleClose = useCallback(() => {
    setShowContent(false);
    setTimeout(() => {
      setCountdown(0);
      onClose();
    }, 500);
  }, [onClose]);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (isOpen && countdown > 0) {
      timerId = setInterval(() => {
        setCountdown((prev) => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            handleClose();
          } 
          return newValue;
        });
      }, 1000);
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [isOpen, countdown, handleClose]);

  // Mise à jour de la barre de progression
  useEffect(() => {
    setProgress((countdown / 20) * 100);
  }, [countdown]);

  if (!isOpen) return null;

  const { invoiceTotal } = orderTotals;

  return (
<div className="fixed inset-0 z-3000 popup-overlay">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-md">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-red-500/10 to-yellow-500/10 animate-gradient" />
      </div>

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div
          className={`w-full max-w-md transform transition-all duration-500 ${
            showContent ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}
        >
          <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
            {error && (
              <div className="p-4 bg-red-100 text-red-700 rounded-t-lg">
                {error}
              </div>
            )}
            <div className="absolute top-0 left-0 w-full h-2">
              <div
                className="absolute inset-0 bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500 animate-pulse opacity-50"
              />
              <div
                className="absolute h-full bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500"
                style={{ width: `${progress}%`, transition: 'width 1s linear' }}
              />
            </div>

            <div className="p-8 space-y-6 bg-gradient-to-b from-white/5 to-white/10">
              {orderCode && (
                <div className="text-center">
                  <span className="text-lg font-medium text-white">
                    {t('Numéro de commande')}
                  </span>
                  <div className="mt-2">
                    <span className="text-4xl font-bold bg-gradient-to-r from-orange-200 to-yellow-200 bg-clip-text text-transparent">
                      {orderCode}
                    </span>
                  </div>
                </div>
              )}

              {(customerName || tableNumber || deliveryAddress) && (
                <div className="p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20">
                  {customerName && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-500">{t('Client')}:</span>
                      <span className="font-medium text-gray-900">{customerName}</span>
                    </div>
                  )}
                  {tableNumber && orderType === 'dine-in' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-500">{t('Table')}:</span>
                      <span className="font-medium text-gray-900">{tableNumber}</span>
                    </div>
                  )}
                  {deliveryAddress && orderType === 'delivery' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-500">{t('Adresse')}:</span>
                      <span className="font-medium text-gray-900">{deliveryAddress}</span>
                    </div>
                  )}
                  {phoneNumber && orderType === 'delivery' && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm text-gray-500">{t('Téléphone')}:</span>
                      <span className="font-medium text-gray-900">{phoneNumber}</span>
                    </div>
                  )}
                  {complementaryInfo && orderType === 'delivery' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{t('Info complémentaire')}:</span>
                      <span className="font-medium text-gray-900">{complementaryInfo}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-500">{t('Type de commande')}:</span>
                    <span className="font-medium text-gray-900">
                      {orderType === 'takeaway' ? t('À emporter') : orderType === 'dine-in' ? t('Sur place') : t('Livraison')}
                    </span>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">{t('Détails de la commande')}</h3>
                <div className="space-y-4">
                  {items.map((item, index) => {
                    const itemTotal =
                      item.menuItem.price * item.quantity +
                      Object.values(item.customizations).reduce(
                        (sum, options) =>
                          sum + options.reduce((optSum, opt) => optSum + opt.price, 0),
                        0
                      ) * item.quantity;

                    return (
                      <div
                        key={`${item.menuItem.id}-${index}`}
                        className="flex justify-between items-start p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-orange-600">{item.quantity}×</span>
                            <span className="font-medium text-gray-900">{item.menuItem.name}</span>
                          </div>
                          {Object.entries(item.customizations).map(([specId, options], optIndex) => (
                            <div
                              key={`${item.menuItem.id}-${specId}-${optIndex}`}
                              className="text-sm text-gray-500 ml-6"
                            >
                              {options.map((opt, i) => (
                                <span
                                  key={`${item.menuItem.id}-${specId}-${opt.id}-${i}`}
                                >
                                  {opt.name}
                                  {opt.price > 0 && ` (+${opt.price.toFixed(2)}€)`}
                                  {i < options.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          ))}
                        </div>
                        <span className="font-semibold text-gray-900">{itemTotal.toFixed(2)}€</span>
                      </div>
                    );
                  })}
                </div>
              </div>

<div className="space-y-3 p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-orange-100/20">
  <div>
    <div className="text-gray-600">{t('Appelez si vous avez besoin de plus d\'information')}</div>
    <div className="flex items-center gap-2">
      <span>📞</span>
      <span className="text-orange-600">{t('03 23 64 01 67')}</span>
    </div>
  </div>
  <div className="flex justify-between text-xl font-bold pt-3 border-t border-orange-100/20">
    <span>{t('Total')}</span>
    <span className="text-orange-600">{invoiceTotal.toFixed(2)}€</span>
  </div>
  {paymentMethod && (
    <div className="flex justify-between text-gray-600 mt-2">
      <span>{t('Méthode de paiement')}</span>
      <span>{t(paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1))}</span>
    </div>
  )}
</div>

              <div className="text-center">
                <button
                  onClick={handleClose}
                  className="w-full group relative"
                  aria-label={t('Fermer la confirmation')}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-tilt"></div>
                  <div className="relative px-6 py-3 bg-white rounded-xl flex items-center justify-center gap-2 leading-none">
                    <span className="font-semibold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                      {t('Fermer')}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};